/**
 * Signing in through the identity service.
 *
 * Authorization code with PKCE against the Cognito hosted UI: the browser is
 * sent to Cognito to sign in (and complete MFA, if the pool requires it), comes
 * back with a one-time code, and swaps it for an ID token here. The token is
 * what every API request carries. Nothing about a password touches this app.
 *
 * In dev mode there is no identity service and none of this runs.
 */
const TOKEN_KEY = 'pts-compass.id-token';
const REFRESH_KEY = 'pts-compass.refresh-token';
const VERIFIER_KEY = 'pts-compass.pkce-verifier';

export interface IdentityInfo {
  mode: 'dev' | 'cognito';
  userPoolId: string | null;
  clientId: string | null;
  region: string | null;
  hostedUiDomain: string | null;
  /** Email domains Compass admits; empty means the gate is open (the development default). */
  allowedEmailDomains: string[];
}

export async function fetchIdentity(): Promise<IdentityInfo> {
  const r = await fetch('/api/v1/identity');
  return (await r.json()) as IdentityInfo;
}

export function getIdToken(): string | null {
  try {
    const t = window.localStorage.getItem(TOKEN_KEY);
    if (!t) return null;
    const payload = JSON.parse(atob(t.split('.')[1] ?? '')) as { exp?: number };
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      window.localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return t;
  } catch {
    return null;
  }
}

/**
 * Keep the sign-in alive without asking again. The ID token lasts an hour; the
 * refresh token the app client issues lasts thirty days. When the ID token is
 * about to run out and a refresh token is held, a new ID token is fetched from
 * the identity service in the background. Returns true when a valid ID token
 * is in hand afterwards. Concurrent callers share one refresh.
 */
let refreshing: Promise<boolean> | null = null;
export function ensureFreshToken(info: IdentityInfo | null): Promise<boolean> {
  if (!info || info.mode !== 'cognito') return Promise.resolve(true);
  const t = window.localStorage.getItem(TOKEN_KEY);
  if (t) {
    try {
      const payload = JSON.parse(atob(t.split('.')[1] ?? '')) as { exp?: number };
      if (!payload.exp || payload.exp * 1000 > Date.now() + 90_000) return Promise.resolve(true);
    } catch { /* fall through to a refresh */ }
  }
  const refreshToken = window.localStorage.getItem(REFRESH_KEY);
  if (!refreshToken || !info.hostedUiDomain || !info.clientId) return Promise.resolve(Boolean(getIdToken()));
  refreshing ??= (async () => {
    try {
      const body = new URLSearchParams({ grant_type: 'refresh_token', client_id: info.clientId!, refresh_token: refreshToken });
      const r = await fetch(`https://${info.hostedUiDomain}/oauth2/token`, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body });
      if (!r.ok) {
        // A revoked or expired refresh token means a real sign-in is needed.
        if (r.status === 400 || r.status === 401) window.localStorage.removeItem(REFRESH_KEY);
        return Boolean(getIdToken());
      }
      const tokens = (await r.json()) as { id_token?: string; refresh_token?: string };
      if (!tokens.id_token) return Boolean(getIdToken());
      window.localStorage.setItem(TOKEN_KEY, tokens.id_token);
      if (tokens.refresh_token) window.localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
      return true;
    } catch {
      return Boolean(getIdToken());
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export function tokenClaims(): Record<string, unknown> | null {
  const t = getIdToken();
  if (!t) return null;
  try {
    return JSON.parse(atob(t.split('.')[1] ?? '')) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function beginSignIn(info: IdentityInfo): Promise<void> {
  if (!info.hostedUiDomain || !info.clientId) throw new Error('The identity service is not configured');
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
  window.sessionStorage.setItem(VERIFIER_KEY, verifier);
  const params = new URLSearchParams({
    response_type: 'code', client_id: info.clientId, redirect_uri: `${window.location.origin}/auth/callback`,
    scope: 'openid email profile', code_challenge_method: 'S256', code_challenge: base64url(digest),
  });
  window.location.assign(`https://${info.hostedUiDomain}/oauth2/authorize?${params.toString()}`);
}

export async function completeSignIn(info: IdentityInfo, code: string): Promise<void> {
  const verifier = window.sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier || !info.hostedUiDomain || !info.clientId) throw new Error('Sign-in did not start here');
  const body = new URLSearchParams({
    grant_type: 'authorization_code', client_id: info.clientId, code, code_verifier: verifier,
    redirect_uri: `${window.location.origin}/auth/callback`,
  });
  const r = await fetch(`https://${info.hostedUiDomain}/oauth2/token`, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body });
  if (!r.ok) throw new Error(`The identity service refused the sign-in (${r.status})`);
  const tokens = (await r.json()) as { id_token?: string; refresh_token?: string };
  if (!tokens.id_token) throw new Error('No ID token was returned');
  window.localStorage.setItem(TOKEN_KEY, tokens.id_token);
  if (tokens.refresh_token) window.localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
  window.sessionStorage.removeItem(VERIFIER_KEY);
}

export function signOut(info: IdentityInfo | null): void {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(REFRESH_KEY);
  if (info?.hostedUiDomain && info.clientId) {
    const params = new URLSearchParams({ client_id: info.clientId, logout_uri: window.location.origin });
    window.location.assign(`https://${info.hostedUiDomain}/logout?${params.toString()}`);
  } else {
    window.location.assign('/');
  }
}
